import React, { useState } from 'react';
import { ShieldCheck, Zap, UserCheck, ArrowRight, X, HelpCircle } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from './ui/popover';

const KEY = 'dh_howitworks_dismissed';

const STEPS = [
  { icon: ShieldCheck, title: 'Agents verify every appointment', desc: '270/271 runs automatically when a visit is booked.' },
  { icon: Zap, title: 'Low-risk clears itself', desc: 'Clean, in-network, deductible-met checks auto-clear.' },
  { icon: UserCheck, title: 'You handle the exceptions', desc: 'Only flagged visits reach you — with the next action ready.' },
];

// Compact form of the product spine: a quiet "How this works" header link that
// reveals the same three steps in a popover. Replaces the full-width band so the
// narrative stays available without occluding the queue. A subtle brand dot draws
// the eye until the user has opened it once (persisted in localStorage).
export function HowItWorksPopover() {
  const [seen, setSeen] = useState(() => {
    try { return localStorage.getItem(KEY) === '1'; } catch { return true; }
  });
  const markSeen = () => { try { localStorage.setItem(KEY, '1'); } catch {} setSeen(true); };

  return (
    <Popover onOpenChange={(o) => { if (o) markSeen(); }}>
      <PopoverTrigger asChild>
        <button className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-slate-800 transition-colors">
          <HelpCircle className="h-3.5 w-3.5" />
          How this works
          {!seen && <span className="h-1.5 w-1.5 rounded-full bg-brand-500 dh-pulse" />}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-4">
        <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-3">How Due Health works</div>
        <div className="space-y-3">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={i} className="flex items-start gap-3">
                <div className="h-8 w-8 rounded-lg bg-brand-50 text-brand-600 flex items-center justify-center shrink-0"><Icon className="h-4 w-4" /></div>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-900 leading-snug">{i + 1}. {s.title}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{s.desc}</div>
                </div>
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// One-time narrative strip: makes the product spine obvious on first login.
export default function HowItWorks() {
  const [show, setShow] = useState(() => {
    try { return localStorage.getItem(KEY) !== '1'; } catch { return true; }
  });
  if (!show) return null;
  const dismiss = () => { try { localStorage.setItem(KEY, '1'); } catch {} setShow(false); };

  return (
    <div className="dh-card p-4 relative">
      <button onClick={dismiss} className="absolute top-3 right-3 text-slate-300 hover:text-slate-600"><X className="h-4 w-4" /></button>
      <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-3">How Due Health works</div>
      <div className="grid sm:grid-cols-3 gap-3">
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          return (
            <div key={i} className="flex items-start gap-3 relative">
              <div className="h-8 w-8 rounded-lg bg-brand-50 text-brand-600 flex items-center justify-center shrink-0"><Icon className="h-4 w-4" /></div>
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-900 leading-snug">{i + 1}. {s.title}</div>
                <div className="text-xs text-slate-500 mt-0.5">{s.desc}</div>
              </div>
              {i < STEPS.length - 1 && <ArrowRight className="hidden sm:block h-4 w-4 text-slate-300 absolute -right-2.5 top-2" />}
            </div>
          );
        })}
      </div>
    </div>
  );
}
